// Tree generator implementation from Sven Moen's 'Drawing Dynamic Trees'
// 12/06/2024

const PARENT_DISTANCE = 30;
const NODE_WIDTH = 30;
const NODE_HEIGHT = 30;
const BORDER = 50;

class Line{
    constructor(dx, dy, link = null){
        this.dx = dx; 
        this.dy = dy;
        this.link_id = link;
    }
}

class Polygon{
    constructor(){
        this.lower = {head:null, tail:null};
        this.upper = {head:null, tail:null};
    }
}

class Node{
    constructor(id, {label='something', width=NODE_WIDTH, height = NODE_HEIGHT, border = BORDER, x = 0, y = 0} = {}, parent=null, child=null, sibling=null, offsetX = 0, offsetY = 0)
    {
        this.id = id;
        this.label = label;
        this.parent = parent;
        this.child = child;
        this.sibling = sibling;
        this.width = width;
        this.height = height;
        this.border = border;
        this.pos = {x:x, y:y};
        this.offset = {x:offsetX, y:offsetY};
        this.contour = new Polygon();
    }
}

function treeGen(tree_root)
{
    let c;
    for(c = tree_root.child; c; c=c.sibling)
    {
        treeGen(c);
    }

    if(tree_root.child)
    {
        attachParent(tree_root, join(tree_root));
    } else {
        layoutLeaf(tree_root);
    }
}

function attachParent(tree, height)
{
    let x, y1, y2;
    x = tree.border + PARENT_DISTANCE;
    y2 = (height - tree.height)/2 - tree.border;
    y1 = y2 + tree.height + 2*tree.border - height;
    tree.child.offset.x = x + tree.width;
    tree.child.offset.y = y1;
    tree.contour.upper.head = new Line(tree.width, 0, new Line(x, y1, tree.contour.upper.head));
    tree.contour.lower.head = new Line(tree.width, 0, new Line(x, y2, tree.contour.lower.head))
}

function join(tree)
{
    let c;
    let d, h, sum;

    c = tree.child;
    tree.contour = c.contour;
    sum = h = c.height + 2*c.border;
    c = c.sibling;
    while(c)
    {
        d = merge(tree.contour, c.contour);
        c.offset.y = d+h;
        c.offset.x = 0;
        h = c.height + 2*c.border;
        sum += d + h;
        c = c.sibling;
    }
    return sum;
}

function merge(con1, con2)
{
    let x, y, total, d;
    let lower, upper, b;

    x = y = total = 0;
    upper = con1.lower.head;
    lower = con2.lower.head;

    while(lower && upper)
    {
        d = offset(x, y, lower.dx, lower.dy, upper.dx, upper.dy);
        y += d;
        total += d;

        if (x+lower.dx <= upper.dx)
        {
            y += lower.dy;
            x += lower.dx;
            lower = lower.link;
        } else {
            y -= upper.dy;
            x -= upper.dx;
            upper = upper.link;
        }
    }

    if (lower) {
        b = bridge(con1.upper.tail, 0, 0, lower, x, y);
        con1.upper.tail = (b.link) ? con2.upper.tail : b;
        con1.lower.tail = con2.lower.tail;
    } else {
        b = bridge(con2.lower.tail, x, y, upper, 0, 0);
        if (!b.link)
        {
            con1.lower.tail = b;
        }
    }
    con1.lower.head = con2.lower.head;

    return total;
}

function offset(p1, p2, a1, a2, b1, b2)
{
    let d,s,t;
    
    if (b1 <= p1 || p1 + a1 <= 0) return 0;
    
    t = b1*a2 - a1*b2;
    if (t > 0)
    {
        if (p1 < 0)
        {
            s = p1*a2;
            d = s/a1 - p2;
        } else if (p1 > 0)
        {
            s = p1*b2;
            d = s/b1 - b2;
        } else {
            d = -p2;
        }
    } else if (b1 < p1+a1)
    {
        s = (b1-p1)*a2;
        d = b2 - (p2 + s/a1);
    } else if (b1 > p1+a1)
    {
        s = (a1+p1)*b2;
        d = s/b1 - (p2 + a2);
    } else {
        d = b2 - (p2+a2);
    }

    return Math.max(0, d);
}

function bridge(line1, x1, y1, line2, x2, y2)
{
    let dy, dx, s;
    let r;
    
    dx = x2 + line2.dx - x1;
    if (line2.dx = 0)
    {
        dy = line2.dy;
    } else
    {
        s = dx * line2.dy;
        dy = s/line2.dx;
    }
    r = new Line(dx, dy, line2.link);
    line1.link = new Line(0, y2 + line2.dy - dy - y1, r);
    return r;
}

function layoutLeaf(tree)
{
    tree.contour.upper.tail = new Line(tree.width + 2*tree.border, 0, 0);
    tree.contour.upper.head = new Line(tree.width + 2*tree.border, 0, 0);
    tree.contour.lower.tail = new Line(0, -tree.height - 2*tree.border, 0);
    tree.contour.lower.head = new Line(tree.width + 2*tree.border, 0, tree.contour.lower.tail);
}

function drawTree(root)
{
    let nodes = new Map();
    
    let nodeStack = [root];
    let curNode;

    while (nodeStack.length > 0)
    {
        curNode = nodeStack.pop();

        if(curNode.sibling)
        {
            curNode.sibling.pos.x = curNode.pos.x + curNode.sibling.offset.x;
            curNode.sibling.pos.y = curNode.pos.y + curNode.sibling.offset.y;
            nodeStack.push(curNode.sibling);
        }
        if(curNode.child)
        {
            curNode.child.pos.x = curNode.pos.x + curNode.child.offset.x;
            curNode.child.pos.y = curNode.pos.y + curNode.child.offset.y;
            nodeStack.push(curNode.child);
        }
        nodes.set(curNode.id, {x:curNode.pos.x, y:curNode.pos.y});
    }
    return nodes;
}

function generateTreeLayout(data)
{
    const [nodes, edges] = data;
    let treeNodes = nodes.map(node => new Node(node.id));
    let nodeMap = new Map();
    treeNodes.forEach(node => nodeMap.set(node.id, node));

    edges.forEach(edge => {
        let from, to;
        
        if(edge.sidelnk)
        {
            from = edge.to;
            to = edge.from;
        } else {
            from = edge.from;
            to = edge.to;
        }
        let parentNode = nodeMap.get(from);
        let childNode = nodeMap.get(to);
        if (parentNode && childNode)
        {
            if (!parentNode.child)
            {
                parentNode.child = childNode;
            } else {
                let sibling = parentNode.child;
                while (sibling.sibling) {
                    sibling = sibling.sibling;
                }
                sibling.sibling = childNode;
            }
            childNode.parent = parentNode;
        }
    });

    let root = treeNodes.find(node => !node.parent)
    treeGen(root);
    return drawTree(root);
}

export default generateTreeLayout;